import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Wrench, Plus, Search, Factory, LifeBuoy, ShieldCheck, Recycle, Rotate3D, Rocket, FlaskConical, Hammer, Tag } from 'lucide-react';

const TYPE_META = {
  new_production:       { icon: Factory,       label: 'New Production',      color: 'bg-sky-100 text-sky-800 border-sky-200' },
  customer_repair:      { icon: Wrench,        label: 'Customer Repair',     color: 'bg-amber-100 text-amber-800 border-amber-200' },
  warranty_repair:      { icon: ShieldCheck,   label: 'Warranty Repair',     color: 'bg-blue-100 text-blue-800 border-blue-200' },
  extended_warranty:    { icon: ShieldCheck,   label: 'Extended Warranty',   color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  refurbishment:        { icon: Recycle,       label: 'Refurbishment',       color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rework:               { icon: Rotate3D,      label: 'Rework',              color: 'bg-orange-100 text-orange-800 border-orange-200' },
  upgrade_modification: { icon: Hammer,        label: 'Upgrade / Mod',       color: 'bg-violet-100 text-violet-800 border-violet-200' },
  prototype_rnd:        { icon: FlaskConical,  label: 'Prototype / R&D',     color: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200' },
  internal_maintenance: { icon: Wrench,        label: 'Internal Maintenance',color: 'bg-slate-100 text-slate-700 border-slate-200' },
  warranty_replacement: { icon: Rocket,        label: 'Warranty Replace',    color: 'bg-rose-100 text-rose-800 border-rose-200' },
};
const STATUS_COLOR = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-200',
  Released: 'bg-blue-100 text-blue-800 border-blue-200',
  InProgress: 'bg-amber-100 text-amber-800 border-amber-200',
  Hold: 'bg-rose-100 text-rose-800 border-rose-200',
  QC: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  Complete: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Closed: 'bg-slate-900 text-white border-slate-900',
  Canceled: 'bg-red-100 text-red-800 border-red-200',
};

const WorkOrdersPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('__all__');
  const [typeFilter, setTypeFilter] = useState('__all__');
  const [meta, setMeta] = useState({ types: [], statuses: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== '__all__') params.append('status', status);
      if (typeFilter && typeFilter !== '__all__') params.append('wo_type', typeFilter);
      if (q.trim()) params.append('q', q.trim());
      const [list, m] = await Promise.all([
        api().get(`/work-orders-v2?${params.toString()}`),
        api().get('/work-orders-v2/meta'),
      ]);
      setRows(list.data || []);
      setMeta(m.data || { types: [], statuses: [] });
    } finally { setLoading(false); }
  }, [api, q, status, typeFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="work-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Wrench className="w-6 h-6 text-slate-700" /> Work Orders
            <Badge variant="outline" className="rounded-sm text-[10px] tracking-wider">Peak Prairie</Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            One unified doc for Production, Repair, Warranty, Refurb, Rework, Upgrade, R&amp;D &amp; Internal Maintenance.
            Legacy POs/ROs still live under their own pages during the shadow phase.
          </p>
        </div>
        <Button onClick={() => navigate('/orders/work/new')} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="wo-v2-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Work Order
        </Button>
      </div>

      <Card className="industrial-card">
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-56">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search WO#, customer, product, RMA…" className="pl-8 rounded-sm" data-testid="wo-v2-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="rounded-sm w-56" data-testid="wo-v2-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All types</SelectItem>
              {meta.types.map(t => <SelectItem key={t} value={t}>{TYPE_META[t]?.label || t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm w-40" data-testid="wo-v2-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {meta.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">{rows.length} work order{rows.length === 1 ? '' : 's'}</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : !rows.length ? (
            <div className="p-6 text-sm text-slate-500 italic">
              No Work Orders yet. Click <strong>New Work Order</strong> to create one, or run the backfill script to pull existing POs/ROs:
              <div className="mono text-xs bg-slate-100 border border-slate-200 rounded-sm px-2 py-1 mt-2 inline-block">cd /app/backend &amp;&amp; python -m scripts.backfill_work_orders_v2</div>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>WO #</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
                <TableHead>Customer</TableHead><TableHead>Product</TableHead><TableHead>Priority</TableHead>
                <TableHead>Opened</TableHead><TableHead className="text-right">Qty</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map(r => {
                  const T = TYPE_META[r.wo_type] || { icon: Tag, label: r.wo_type, color: '' };
                  const TypeIcon = T.icon;
                  return (
                    <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/work/${r.id}`)} data-testid={`wo-v2-row-${r.wo_number}`}>
                      <TableCell className="mono font-semibold">{r.wo_number}</TableCell>
                      <TableCell><Badge className={`${T.color} border rounded-sm text-[11px]`}><TypeIcon className="w-3 h-3 mr-1 inline" />{T.label}</Badge></TableCell>
                      <TableCell><Badge className={`${STATUS_COLOR[r.status] || ''} border rounded-sm text-[11px]`}>{r.status}</Badge></TableCell>
                      <TableCell className="text-sm">{r.customer_name_snapshot || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-sm mono">{r.product_model || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-xs">{r.priority}</TableCell>
                      <TableCell className="text-xs mono">{r.date_opened}</TableCell>
                      <TableCell className="text-right mono">{r.quantity ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkOrdersPage;
